"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_library_1 = require("@seneca/client-library");
const schools_service_types_1 = require("@seneca/schools-service-types");
const BadRequestError_1 = __importDefault(require("./errors/BadRequestError"));
const BatchFetchEntitiesByExternalIdDoesNotExistError_1 = __importDefault(require("./errors/BatchFetchEntitiesByExternalIdDoesNotExistError"));
const ForbiddenForSchoolError_1 = __importDefault(require("./errors/ForbiddenForSchoolError"));
const ForbiddenAccessToSchoolEntitiesError_1 = __importDefault(require("./errors/ForbiddenAccessToSchoolEntitiesError"));
const SchoolInfoBySchoolIdDoesNotExistError_1 = __importDefault(require("./errors/SchoolInfoBySchoolIdDoesNotExistError"));
const SchoolInfoByUrnDoesNotExistError_1 = __importDefault(require("./errors/SchoolInfoByUrnDoesNotExistError"));
const UserSchoolInfoDoesNotExistError_1 = __importDefault(require("./errors/UserSchoolInfoDoesNotExistError"));
const SchoolNotFoundError_1 = __importDefault(require("./errors/SchoolNotFoundError"));
/**
 * SchoolsServiceClient
 * @param {Object} options options object input data and dependency injection
 * @param {Object} options.fetch change fetch method from whatwg-fetch (optional, default: whatwg-fetch)
 * @param {string} options.courseId saves entering courseId required for some methods
 * @param {string} options.protocol the http protocol of the service endpoint
 * @param {string} options.host the host of the service endpoint
 * @param {string} options.path the path of the service endpoint (optional)
 * @param {number} options.retries the number of times the client should retry on network error / service error (optional, default: 3)
 * @param {number} options.delayInSecs the delay between retries (optional, default: 2)
 * @param {Object} options.credentials identifiers and secrets required to hit protected endpoints (optional)
 * @param {string} options.region states the region of the service for signing (optional, default: eu-west-1)
 * @param {RequestMaker} options.requestMaker for overriding request making logic (optional, default: RequestMaker)
 * @throws {UrlConstructionError} when either options.protocol or options.host is missing
 */
class SchoolsServiceClient extends client_library_1.Client {
    constructor(fetch, options) {
        super(fetch, options);
    }
    /**
     * @param {string} searchTerm - search term to query schools by
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.schools}
     */
    async searchSchoolsList(searchTerm) {
        let url = this._constructUrl('api/schools');
        if (searchTerm) {
            url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        const expectedResponses = [200];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        const body = response.json;
        return body === null || body === void 0 ? void 0 : body.items;
    }
    async fetchMySchoolInfo() {
        const url = this._constructUrl(`api/school-info/me`);
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            throw new UserSchoolInfoDoesNotExistError_1.default();
        }
        return response.json;
    }
    async putMySchoolInfo({ schoolId }) {
        const url = this._constructUrl(`api/school-info/me`);
        const expectedResponses = [200, 422];
        const response = await this.requestMaker.makeRequest(url, { method: 'PUT', body: JSON.stringify({ schoolId }) }, expectedResponses);
        if (response.status === 422) {
            throw new SchoolInfoBySchoolIdDoesNotExistError_1.default(schoolId);
        }
        return response.json;
    }
    async fetchSchoolInfo(schoolId) {
        const url = this._constructUrl(`api/admin/school-info/${schoolId}`);
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            throw new SchoolInfoBySchoolIdDoesNotExistError_1.default(schoolId);
        }
        return response.json;
    }
    async fetchSchoolInfoByUrn(urn) {
        const url = this._constructUrl(`api/admin/school-info/urn/${urn}`);
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            throw new SchoolInfoByUrnDoesNotExistError_1.default(urn);
        }
        return response.json;
    }
    async batchSaveUserSchools(userSchools) {
        const url = this._constructUrl(`api/admin/school-info/users`);
        const expectedResponses = [200];
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ userSchools }) }, expectedResponses);
        return response.json;
    }
    async fetchUserSchoolInfo(userId) {
        const url = this._constructUrl(`api/admin/school-info/users/${userId}`);
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            throw new UserSchoolInfoDoesNotExistError_1.default();
        }
        return response.json;
    }
    async fetchMyTeacherSchools() {
        const url = this._constructUrl('api/teacher/schools/me');
        const expectedResponses = [200];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        const body = response.json;
        return body.items;
    }
    async getAllTeacherClasses() {
        const url = this._constructUrl(`api/teachers/classes`);
        const expectedResponses = [200];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        const body = response.json;
        return body.items;
    }
    async getSchoolClassesAdmin(schoolId, teacherId) {
        const url = this._constructUrl(`api/admin/schools/${schoolId}/classes?teacherId=${teacherId}`);
        const expectedResponses = [200];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        const body = response.json;
        return body.items;
    }
    async getSchoolClassesTeacher(schoolId, { limit = 1000 } = {}) {
        let classes = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfSchoolClassesTeacher(schoolId, {
                lastKey,
                limit
            });
            classes = classes.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return classes;
    }
    async getSchoolTeachers(schoolId, { limit = 1000 } = {}) {
        let teachers = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfSchoolUserEntities(schools_service_types_1.SchoolEntityTypes.Teacher)(schoolId, {
                lastKey,
                limit
            });
            teachers = teachers.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return teachers;
    }
    async getSchoolStudents(schoolId, { limit = 1000 } = {}) {
        let students = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfSchoolUserEntities(schools_service_types_1.SchoolEntityTypes.Student)(schoolId, {
                lastKey,
                limit
            });
            students = students.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return students;
    }
    async getSchoolInvalidTeachers(schoolId, { limit = 1000 } = {}) {
        let invalidTeachers = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfSchoolUserEntities(schools_service_types_1.SchoolEntityTypes.InvalidTeacher)(schoolId, {
                lastKey,
                limit
            });
            invalidTeachers = invalidTeachers.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return invalidTeachers;
    }
    async getSchoolInvalidStudents(schoolId, { limit = 1000 } = {}) {
        let invalidStudents = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfSchoolUserEntities(schools_service_types_1.SchoolEntityTypes.InvalidStudent)(schoolId, {
                lastKey,
                limit
            });
            invalidStudents = invalidStudents.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return invalidStudents;
    }
    async fetchPageOfSchoolClassesTeacher(schoolId, { limit, lastKey }) {
        const url = this._constructUrl(`api/schools/${schoolId}/classes`, {
            limit,
            lastKey
        });
        const expectedResponses = [200];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        return response.json;
    }
    getSchoolUserEntitiesFetchUrlEnding(type) {
        switch (type) {
            case schools_service_types_1.SchoolEntityTypes.Student:
                return 'students';
            case schools_service_types_1.SchoolEntityTypes.Teacher:
                return 'teachers';
            case schools_service_types_1.SchoolEntityTypes.InvalidStudent:
                return 'students/invalid';
            case schools_service_types_1.SchoolEntityTypes.InvalidTeacher:
                return 'teachers/invalid';
            default:
                throw new Error('Invalid school user entity type');
        }
    }
    fetchPageOfSchoolUserEntities(type) {
        return async (schoolId, { limit, lastKey }) => {
            const url = this._constructUrl(`api/schools/${schoolId}/${this.getSchoolUserEntitiesFetchUrlEnding(type)}`, {
                limit,
                lastKey
            });
            const expectedResponses = [200, 403];
            const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
            if (response.status === 403) {
                throw new ForbiddenAccessToSchoolEntitiesError_1.default(schoolId);
            }
            return response.json;
        };
    }
    async joinSchoolClass({ schoolId, classId }) {
        const url = this._constructUrl(`api/schools/joinClass`);
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ schoolId, classId })
        }, expectedResponses);
        if (response.status === 403) {
            throw new ForbiddenForSchoolError_1.default(schoolId);
        }
        return response.json;
    }
    async joinSchoolClasses({ schoolId, classIds }) {
        const url = this._constructUrl(`api/schools/joinClasses`);
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ schoolId, classIds })
        }, expectedResponses);
        if (response.status === 403) {
            throw new ForbiddenForSchoolError_1.default(schoolId);
        }
        return response.json;
    }
    async inviteAllSchoolParents(schoolId) {
        const url = this._constructUrl(`api/schools/${schoolId}/invite-parents`);
        const expectedResponses = [204, 400, 403];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'post'
        }, expectedResponses);
        if (response.status === 403) {
            throw new ForbiddenForSchoolError_1.default(schoolId);
        }
        if (response.status === 400) {
            throw new BadRequestError_1.default('schoolId', 'missing schoolId path parameter');
        }
        return response.json;
    }
    async batchResetUserPasswords({ schoolId, userIds, newPassword }) {
        const url = this._constructUrl(`api/schools/${schoolId}/reset-user-password/batch`);
        const expectedResponses = [200, 400, 403, 422];
        const originalRetries = this.retries;
        this.retries = 0;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'post',
            body: JSON.stringify({ userIds, newPassword })
        }, expectedResponses);
        if (response.status === 403) {
            throw new ForbiddenAccessToSchoolEntitiesError_1.default(schoolId);
        }
        if (response.status === 400) {
            throw new BadRequestError_1.default('schoolId', 'missing schoolId path parameter');
        }
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        this.retries = originalRetries;
        return response.json;
    }
    async batchSaveSchoolEntities(schoolId, entities) {
        const url = this._constructUrl(`api/admin/schools/${schoolId}/entities`);
        const expectedResponses = [200, 422];
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify(entities) }, expectedResponses);
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async writeSchoolInvalidUserEntities({ schoolId, invalidStudents, invalidTeachers }) {
        const url = this._constructUrl(`api/admin/schools/${schoolId}/entities/invalid`);
        const expectedResponses = [200, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'PUT',
            body: JSON.stringify({ invalidStudents, invalidTeachers })
        }, expectedResponses);
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async batchDeleteSchoolEntities(schoolId, entities) {
        const url = this._constructUrl(`api/admin/schools/${schoolId}/entities`);
        const expectedResponses = [200, 404, 422];
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE', body: JSON.stringify(entities) }, expectedResponses);
        if (response.status === 404) {
            throw new SchoolNotFoundError_1.default(schoolId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
    }
    async saveSchool({ schoolId, name, externalId }) {
        const url = this._constructUrl(`api/admin/schools/${schoolId}`);
        const expectedResponses = [200, 403, 422];
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ name, externalId }) }, expectedResponses);
        if (response.status === 403) {
            throw new SchoolInfoBySchoolIdDoesNotExistError_1.default(schoolId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async saveSchoolByExternalId({ name, externalId, urn, parentInformationAvailable, timeLastSynced, syncExpiryDate, subscriptionExpiryDate }) {
        const url = this._constructUrl(`api/admin/schools/external/${externalId}`);
        const expectedResponses = [200, 403, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                name,
                urn,
                parentInformationAvailable,
                timeLastSynced,
                syncExpiryDate,
                subscriptionExpiryDate
            })
        }, expectedResponses);
        if (response.status === 403) {
            throw new SchoolInfoByUrnDoesNotExistError_1.default(urn);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async batchFetchSchoolEntitiesByExternalId({ externalId, type, limit, lastKey }) {
        const url = this._constructUrl(`api/admin/schools/external/${externalId}/entities`, {
            limit,
            lastKey,
            type
        });
        const expectedResponses = [200, 404, 422];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            throw new BatchFetchEntitiesByExternalIdDoesNotExistError_1.default();
        }
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async batchDeleteUserSchools(userIds) {
        const url = this._constructUrl('api/admin/user-schools');
        const expectedResponses = [200, 422];
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE', body: JSON.stringify({ userIds }) }, expectedResponses);
        if (response.status === 422) {
            const body = response.json;
            throw new BadRequestError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    _constructUrl(path, queryParams) {
        let url = this.url;
        if (!url.endsWith('/')) {
            url += '/';
        }
        url += path;
        if (queryParams && Object.keys(queryParams).length > 0) {
            const queryParamParts = Object.keys(queryParams)
                .filter(param => !!queryParams[param])
                .map(param => `${param}=${encodeURIComponent(queryParams[param])}`);
            return `${url}?${queryParamParts.join('&')}`;
        }
        return url;
    }
}
exports.default = SchoolsServiceClient;
//# sourceMappingURL=SchoolsServiceClient.js.map